import React, { useEffect, useMemo, useRef, useState } from "react";
import "./refermodal.css"
import { useAccount, useReadContract } from "wagmi";
import { notifySuccess } from "../../../hooks/utils";
import { BIGCOINMAIN_ADDRESS, TOKEN_SYMBOL } from "../../../utils/constants";
import BigcoinMainContractABI from "../../../abis/bigcoinmain.abi.json";
import { formatUnits, zeroAddress } from "viem";
import { useTranslation } from "react-i18next";

interface ReferModalProps {
  open: boolean;
  onClose: () => void;
}

const ReferModal: React.FC<ReferModalProps> = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const account = useAccount();

  const { data: userReferrals } = useReadContract({
    address: BIGCOINMAIN_ADDRESS,
    abi: BigcoinMainContractABI,
    functionName: 'getReferrals',
    args: [account ? account.address : zeroAddress as any]
  });

  const totalUserReferrals = useMemo(() => {
    if (userReferrals) {
      // get length of userReferrals array
      const length = (userReferrals as any).length;
      return length;
    }
    return 0;
  }, [userReferrals]);

  const { data: userReferralEarnings } = useReadContract({
    address: BIGCOINMAIN_ADDRESS,
    abi: BigcoinMainContractABI,
    functionName: 'referralBonusPaid',
    args: [account ? account.address : zeroAddress as any]
  });

  const userReferralEarningsFormatted = useMemo(() => {
    if (userReferrals) {
      return Number(formatUnits(userReferralEarnings as any ?? "0", 18)).toFixed(2);
    }
    return "0.00";
  }, [userReferrals]);

  const { data: referralFee } = useReadContract({
    address: BIGCOINMAIN_ADDRESS,
    abi: BigcoinMainContractABI,
    functionName: 'referralFee',
    args: []
  });

  const referralFeeFormatted = useMemo(() => {
    if (userReferrals) {
      return Number(formatUnits(referralFee as any ?? "0", 18)) * 100;
    }
    return 0;
  }, [referralFee]);


  // MODAL OPEN AND CLOSE
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose]);

  // onclick function to copy the wallet address to clipboard
  const copyToClipboard = () => {
    if (account) {
      navigator.clipboard.writeText(`https://milkit.finance?ref=${account.address}`).then(() => {
        notifySuccess("Referral link copied to clipboard!");
      }).catch((err) => {
        console.error("Failed to copy: ", err);
      });
    }
  };

  if (!open) return null;

  return (
    <div data-state="open" className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ pointerEvents: "auto" }} data-aria-hidden="true" aria-hidden="true">
      <div
        ref={modalRef}
        role="dialog"
        id="radix-:r4f:"
        aria-describedby="radix-:r4h:"
        aria-labelledby="radix-:r4g:"
        data-state="open"
        className="text-sm fixed left-[50%] top-[50%] z-50 grid w-full max-w-modal translate-x-[-50%] translate-y-[-50%] gap-4 border border-slate-200 p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg dark:border-slate-800 dark:bg-slate-950 bg-primary text-white"
        tabIndex={-1}
        style={{ pointerEvents: "auto", border: "none", background: "rgb(16, 44, 57)" }}
      >


        <div className="sc-95105e23-0 ldntJV">
          <h2 className="sc-95105e23-1 hDLfke">{t("refer.title")}</h2>
          <div className="sc-95105e23-2 laBSnh">
            <p className="sc-95105e23-3 hAbkBK">
              {t("refer.desc_p1")} {referralFeeFormatted}% {t("refer.desc_p2")}
              {TOKEN_SYMBOL} {t("refer.desc_p3")}
            </p>
          </div>
          <div className="sc-95105e23-4 dTMIAo">
            <div className="sc-95105e23-5 jpaBGl">
              <div className="sc-95105e23-6 ieNroW">
                <span className="sc-95105e23-7 gPVJgi">{t("refer.total_refs")}</span>
                <span className="sc-95105e23-8 cykPTp">{totalUserReferrals}</span>
              </div>
              <div className="sc-95105e23-6 ieNroW">
                <span className="sc-95105e23-7 gPVJgi">{t("refer.total_earned")}</span>
                <span className="sc-95105e23-8 cykPTp">{userReferralEarningsFormatted} ${TOKEN_SYMBOL}</span>
              </div>
            </div>
            <div className="sc-95105e23-9 geCVnU">
              <span className="sc-95105e23-10 iwXrdA">{t("refer.your_ref_link")}</span>
              <div className="sc-95105e23-11 cWPPMk">
                <span className="sc-95105e23-12 hcTZQO">
                  {account ? `https://milkit.finance?ref=${account.address}` : ""}
                </span>
                <button onClick={copyToClipboard} className="sc-cdc39d4e-0 glfaWa">{t("refer.copy")}</button>
              </div>
            </div>
          </div>
          <div className="sc-95105e23-13 hJjlYa">
            <button className="sc-cdc39d4e-0 gvrgXY" onClick={onClose}>{t("refer.close")}</button>
          </div>
        </div>


      </div>
    </div>
  );
}
export default ReferModal;